import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import type { RefObject } from 'react';
import gsap from 'gsap';
import type { TransitionController, SetupTransitionOptions } from 'util/core-transition-component';
import { useTransitionController } from './useTransitionController';
import useScrollTrigger from '@/hooks/useScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Creates gsap.core.Timeline that will start as soon the component is mounted and component is in-view.
 *
 * @example
 * const elementRef = useRef<HTMLDivElement>(null);
 *
 * // Connect the transitionController to the lifecycle
 * const { inView, transitionController } = useScrollTransition({
 *   scroll: {
 *     trigger: elementRef,
 *     vars: {
 *       onEnter() {
 *         console.log('enter');
 *       }
 *     }
 *   },
 *   setupOptions: () => ({
 *     refs: {
 *       elementRef,
 *     },
 *     setupTransitionInTimeline,
 *   }),
 * });
 *
 * useEffect(() => {
    console.log(inView);
  }, [inView, elementRef]);
 */

export function useScrollTransition<T>(options: {
  scroll: {
    canTransitionIn?: boolean;
    transitionInDelay?: number;
    trigger: RefObject<HTMLElement | null>;
    disableTriggerInOnScroll?: boolean;
    vars?: ScrollTrigger.Vars;
    pin?: RefObject<HTMLElement | null>;
    endTrigger?: RefObject<HTMLElement | null>;
    reverseOnLeaveBack?: boolean;
    enableInView?: boolean;
  };
  setupOptions: () => SetupTransitionOptions<T>;
}): { inView: boolean | undefined; transitionController: TransitionController } {
  const transitionController = useTransitionController(options.setupOptions);

  // if triggerOutOnScroll
  const { inView } = useScrollTrigger({
    trigger: options.scroll.trigger,
    transition: {
      controller: transitionController,
    },
    enableInView: options.scroll.enableInView,
    vars: options.scroll.vars,
    canTransitionIn: options.scroll.canTransitionIn,
    transitionInDelay: options.scroll.transitionInDelay,
    pin: options.scroll.pin,
    endTrigger: options.scroll.endTrigger,
    disabled: options?.scroll?.disableTriggerInOnScroll,
    reverseOnLeaveBack: options.scroll.reverseOnLeaveBack,
  });

  return {
    inView,
    transitionController,
  };
}
